/* OrdersCrudCard.tsx
    Une card affichant la gestion des commandes
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Utilise l'API correspondante pour les opérations de données
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import { faBox } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { OrderData, OrderVersionData } from '../types/types';
import { ordersApi } from '../services/ordersApi';

interface OrdersCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// Retourne la dernière version non annulée d'une commande (la plus haute en versionIndex)
const getLastValidVersion = (order: OrderData): OrderVersionData | null => {
	const validVersions =
		order.orderVersions?.filter((v) => v.status !== 'CANCELED') ?? [];
	if (validVersions.length === 0) return null;
	return validVersions.reduce((prev, curr) =>
		curr.versionIndex > prev.versionIndex ? curr : prev
	);
};

// Même choix que la page /orders/:id/manage : dernière version non annulée,
// sinon la dernière version tout court. Sert uniquement à ouvrir la fiche directement.
const getVersionOpenedByManage = (
	order: OrderData
): OrderVersionData | null => {
	const lastValid = getLastValidVersion(order);
	if (lastValid) return lastValid;
	const versions = order.orderVersions ?? [];
	if (versions.length === 0) return null;
	return versions.reduce((prev, curr) =>
		curr.versionIndex > prev.versionIndex ? curr : prev
	);
};

const getOrderManagePath = (order: OrderData): string => {
	const version = getVersionOpenedByManage(order);
	if (!version) return `/app/orders/${order.id}/manage`;
	return `/app/orderVersions/${version.id}/manage`;
};

// Traduction des statuts de version de commande
const ORDER_STATUS_MAP: Record<string, string> = {
	DRAFT: 'Brouillon',
	VALIDATED: 'Validée',
	CANCELED: 'Annulée',
	ACKNOWLEDGED: 'AR reçu',
	SENT: 'Envoyée',
	PARTIALLY_DELIVERED: 'Partiellement livrée',
	DELIVERED: 'Livrée',
};

// Formatage monétaire EUR
const formatCurrency = (value: number | undefined | null): string => {
	if (value === undefined || value === null) return '—';
	return new Intl.NumberFormat('fr-FR', {
		style: 'currency',
		currency: 'EUR',
	}).format(Number(value));
};

// Formatage de date fr-FR
const formatDate = (dateStr: string | Date | undefined | null): string => {
	if (!dateStr) return '';
	return new Date(dateStr).toLocaleDateString('fr-FR');
};

// OrdersCrudCard component
export const OrdersCrudCard: React.FC<OrdersCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/orders',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	const orderIdsTitle = (a: OrderData): string => {
		const versionId = getLastValidVersion(a)?.id;
		return versionId != null
			? `ID ${a.id} / ID version ${versionId}`
			: `ID ${a.id}`;
	};

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "Référence" avec l'indice entre parenthèses et lien vers la version active
		{
			label: 'Référence (indice)',
			field: 'referenceNumber',
			render: (a: OrderData) => {
				const indice = getLastValidVersion(a)?.versionIndex;
				return indice !== undefined
					? `${a.referenceNumber} (${indice})`
					: a.referenceNumber;
			},
			linkUrl: (a: OrderData): string => getOrderManagePath(a),
			cellTitle: orderIdsTitle,
			exportValue: (a: OrderData): string => {
				const indice = getLastValidVersion(a)?.versionIndex;
				return indice !== undefined
					? `${a.referenceNumber} (${indice})`
					: (a.referenceNumber ?? '');
			},
		},
		// Colonne "Fournisseur" avec lien vers la fiche tiers
		{
			label: 'Fournisseur',
			field: 'tiers.label',
			render: (a: OrderData) => a.tiers?.label ?? '—',
			linkUrl: (a: OrderData): string =>
				a.tiers ? `/app/tiers/${a.tiers.id}/manage` : '',
		},
		// Colonne "État" de la dernière version valide
		{
			label: 'État',
			field: 'none',
			render: (a: OrderData) => {
				const lastVersion = getLastValidVersion(a);
				if (!lastVersion) return '—';
				return (
					ORDER_STATUS_MAP[lastVersion.status ?? ''] ??
					lastVersion.status ??
					'—'
				);
			},
			exportValue: (a: OrderData): string => {
				const lastVersion = getLastValidVersion(a);
				if (!lastVersion) return '';
				return (
					ORDER_STATUS_MAP[lastVersion.status ?? ''] ?? lastVersion.status ?? ''
				);
			},
		},
		// Colonne "Pilote" (assignedUser de la dernière version) — lien cliquable
		{
			label: 'Pilote',
			field: 'none',
			render: (a: OrderData) => {
				const user = getLastValidVersion(a)?.assignedUser;
				if (!user) return '—';
				const fullName = [user.firstName, user.name].filter(Boolean).join(' ');
				return <>{fullName || '—'}</>;
			},
			exportValue: (a: OrderData): string => {
				const user = getLastValidVersion(a)?.assignedUser;
				if (!user) return '';
				return [user.firstName, user.name].filter(Boolean).join(' ');
			},
		},
		// Colonne "Total HT" — getter calculé retourné par le backend depuis les lignes
		{
			label: 'Total HT',
			field: 'none',
			render: (a: OrderData) =>
				formatCurrency(getLastValidVersion(a)?.totalExclTax),
			exportValue: (a: OrderData): number | string => {
				const total = getLastValidVersion(a)?.totalExclTax;
				return total !== undefined ? Number(total) : '';
			},
		},
		// Colonne "Prévue le" — date de livraison prévue
		{
			label: 'Prévue le',
			field: 'none',
			render: (a: OrderData) =>
				formatDate(getLastValidVersion(a)?.deliveryDate) || '—',
			exportValue: (a: OrderData): string =>
				formatDate(getLastValidVersion(a)?.deliveryDate),
		},
	];

	// Colonnes export-only : champs de version non affichés dans le tableau
	const exportOnlyColumns = [
		{
			label: 'ID / ID version',
			exportValue: (a: OrderData): string => {
				const versionId = getLastValidVersion(a)?.id;
				return versionId != null ? `${a.id} / ${versionId}` : String(a.id);
			},
		},
		// Colonne "Imputation" — collecte les codes d'affaire, centres de coûts
		// ou investissements depuis les lignes de la dernière version valide.
		// Dédupliqués et joints par " / " si plusieurs codes distincts sont trouvés.
		{
			label: 'Imputation',
			exportValue: (a: OrderData): string => {
				const lines = getLastValidVersion(a)?.orderLines ?? [];
				const codes = new Set<string>();
				for (const line of lines) {
					if (line.expenseType === 'PROJECT' && line.project?.referenceNumber) {
						codes.add(line.project.referenceNumber);
					} else if (
						line.expenseType === 'OPERATIONAL' &&
						line.costCenter?.code
					) {
						codes.add(line.costCenter.code);
					} else if (line.expenseType === 'CAPITAL' && line.accountingCode) {
						codes.add(line.accountingCode);
					}
				}
				return Array.from(codes).join(' / ');
			},
		},
		{
			label: 'Libellé version',
			exportValue: (a: OrderData): string =>
				getLastValidVersion(a)?.label ?? '',
		},
		{
			label: 'Libellé interne',
			exportValue: (a: OrderData): string =>
				getLastValidVersion(a)?.commentIndex ?? '',
		},
		{
			label: 'Exercice',
			exportValue: (a: OrderData): number | string =>
				getLastValidVersion(a)?.fiscalYear ?? '',
		},
		{
			label: 'Référence fournisseur',
			exportValue: (a: OrderData): string =>
				getLastValidVersion(a)?.tiersReference ?? '',
		},
		{
			label: 'Email fournisseur',
			exportValue: (a: OrderData): string => a.tiers?.email ?? '',
		},
		{
			label: 'Email contact fournisseur',
			exportValue: (a: OrderData): string => {
				const contacts = a.tiers?.contacts ?? [];
				const manager = contacts.find((c) => c.businessManager && c.status);
				return (manager ?? contacts.find((c) => c.status))?.email ?? '';
			},
		},
		{
			label: 'Signataire',
			exportValue: (a: OrderData): string =>
				getLastValidVersion(a)?.signatory ?? '',
		},
		{
			label: "Date d'accusé de réception",
			exportValue: (a: OrderData): string =>
				formatDate(getLastValidVersion(a)?.acknowledgedDate),
		},
		{
			label: 'Date de livraison effective',
			exportValue: (a: OrderData): string =>
				formatDate(getLastValidVersion(a)?.deliveredAt),
		},
		{
			label: "Date d'édition",
			exportValue: (a: OrderData): string =>
				formatDate(getLastValidVersion(a)?.editedAt),
		},
		{
			label: 'Créé le',
			exportValue: (a: OrderData): string =>
				formatDate(getLastValidVersion(a)?.createdAt),
		},
		{
			label: 'Créé par',
			exportValue: (a: OrderData): string => {
				const u = a.createdBy;
				if (!u) return '';
				return [u.firstName, u.name].filter(Boolean).join(' ');
			},
		},
		{
			label: 'Observations',
			exportValue: (a: OrderData): string =>
				getLastValidVersion(a)?.publicNotes ?? '',
		},
	];

	return (
		<GenericCrudCard<OrderData>
			queryKey="orders"
			title={
				size === 'sm'
					? 'Commandes fournisseurs'
					: 'Liste des commandes fournisseurs'
			}
			fetchData={(token, options) => ordersApi.listPaginated(token, options)}
			deleteItem={ordersApi.delete}
			tableColumns={columns}
			exportOnlyColumns={exportOnlyColumns}
			addButtonLabel="Ajouter une commande"
			getAddPath={() => '/app/orders/new'}
			getManageItemPath={(order) => getOrderManagePath(order)}
			emptyMessage="Aucune commande trouvée."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faBox : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};
export default OrdersCrudCard;
